import { Phone, Mail } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Reveal, SectionHeading } from './Reveal';

const FAQS = [
  {
    q: 'What types of properties do you manage?',
    a: 'We work with high-quality 1–5 bed apartments and houses in prime locations that meet the needs of our guests — from corporate clients to holidaying families.',
  },
  {
    q: 'Will my property be kept in good condition?',
    a: 'Absolutely. Unlike traditional lets, our properties are kept to a high-end standard — professionally cleaned multiple times per week, with regular inspections and routine maintenance.',
  },
  {
    q: 'Who stays in the property?',
    a: 'Corporate professionals, business travellers, families on vacation and other trusted, vetted guests. We never allow party groups, students or social housing tenants.',
  },
  {
    q: 'How quickly can we get started?',
    a: "Once we've viewed your property, we aim to provide an offer within 24 hours. Contracts can be signed within 48 hours, and rent payments begin immediately.",
  },
];

export default function Social() {
  return (
    <>
      <section className="theme-light t-bg2 py-28 lg:py-40" data-testid="about-me-section">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="t-border-strong absolute -left-4 -top-4 h-full w-full rounded-2xl border" aria-hidden="true" />
              <div className="group relative overflow-hidden rounded-2xl shadow-[0_24px_60px_rgba(21, 85, 51, 0.18)]">
                <img
                  src="/shadi.jpg"
                  alt="Shadi Omary, Founder of The Gate Stays"
                  data-testid="about-me-photo"
                  className="h-[480px] w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105 sm:h-[540px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#155533]/40 to-transparent" />
                <p className="mono-label absolute bottom-5 left-5 text-[#F3E9D7]/90">Founder · The Gate Stays</p>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionHeading label="About Me" title="The person behind the business" />
            <Reveal delay={0.15}>
              <p className="font-display t-text mt-8 text-2xl font-semibold" data-testid="about-me-name">
                Shadi Omary
              </p>
              <p className="mono-label t-accent-text mt-2">Founder & Managing Director</p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="t-text2 mt-8 space-y-5 text-base font-light leading-relaxed sm:text-lg" data-testid="about-me-bio">
                <p>
                  I spent over two decades as a Software Engineering Director and Product Owner, building and leading
                  international teams across AI, medical, networking, cloud and AdTech, in both corporates and
                  startups. That work taught me that quality comes from reliable systems, attention to detail and
                  taking personal ownership of the outcome.
                </p>
                <p>
                  I started The Gate Stays to bring that same discipline to property. My belief is simple: landlords
                  deserve the same five-star, hands-off service that our corporate guests receive. Every property we
                  take on is one I personally stand behind — inspected, cared for and paid for on time, every month.
                </p>
                <p className="t-soft font-normal">When you work with us, you deal directly with me.</p>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="tel:+447425688476"
                  data-testid="about-me-phone"
                  className="t-card t-border hover-lift flex items-center gap-3 rounded-full border px-6 py-3 text-sm font-medium"
                >
                  <Phone size={15} className="t-accent-text" />
                  <span className="t-text">07425 688476</span>
                </a>
                <a
                  href="mailto:info@thegatestays.co.uk"
                  data-testid="about-me-email"
                  className="t-card t-border hover-lift flex items-center gap-3 rounded-full border px-6 py-3 text-sm font-medium"
                >
                  <Mail size={15} className="t-accent-text" />
                  <span className="t-text">info@thegatestays.co.uk</span>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="faq" className="theme-light t-bg py-28 lg:py-40" data-testid="faq-section">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading label="FAQ" title="Answers, before you ask" />
            <Reveal delay={0.15}>
              <p className="t-muted mt-6 text-sm font-light leading-relaxed">
                Anything else on your mind? Submit an enquiry and a member of our team will respond within one working
                day.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="t-border border-b"
                  data-testid={`faq-accordion-item-${i}`}
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="font-display t-text py-7 text-left text-lg font-medium transition-colors duration-300 hover:text-[var(--c-accent-text)] hover:no-underline sm:text-xl [&[data-state=open]]:text-[var(--c-accent-text)]"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="t-text2 pb-7 text-sm font-light leading-relaxed sm:text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
